// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { rem } from 'polished';
import React, { FC, useRef } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { BigPictureClient, Event } from '@vimeo/bigpicture-client';
import { Avatar, Box } from '@vimeo/bokeh';
import { fromNow } from '@vimeo/core/libs/date';
import { translate } from '@vimeo/i18n-runtime';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { markActivityAcknowledged, toggleFollow } from './actions';
import FollowButton from './components/FollowButton';
import LinkCTA from './components/LinkCTA';
import Reaction from './components/Reaction';
import { FA_COMPONENTS } from './constants';
import { trackFa } from './logging';
import { Activity as ActivityType, ActivityState, FATracking } from './types';

type Props = {
  activity: ActivityType;
  markActivityAcknowledged: (id: string) => void;
  toggleFollow: (userId: number) => void;
  showCta: boolean;
  pendingFollows: {
    [key: string]: boolean;
  };
  fatalAttraction: FATracking;
};

const ActivityContainer: FC<Props> = (props: Props) => {
  const {
    activity: { displayed, text, timestamp, url, subject_thumbnail, subject_url },
  } = props;

  const getFAForComponent = (component: string) => ({
    ...props.fatalAttraction,
    component: (FA_COMPONENTS as any)[component],
  });

  const wrapperRef = useRef<HTMLDivElement>(null);

  const trackBP = () => {
    BigPictureClient.sendEvent(
      new Event('click_activity_bell_item', 2, {
        product: 'Workflow',
        path: window.location.pathname,
        location: 'activity bell',
        activity_type: props.activity.type,
        body: props.activity.text,
        destination: props.activity.url,
        first_click: !props.activity.displayed,
      }),
    );
  };

  const handleActivityClick = (url: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackFa(props.fatalAttraction);
    trackBP();

    props.markActivityAcknowledged(props.activity.id);
    e.currentTarget.href = url.includes('?')
      ? `${url}&activityReferer=1`
      : `${url}?activityReferer=1`;
  };

  const handleFollowCtaClick = () => {
    const { id, cta_follower_id, cta_is_following } = props.activity;
    trackFa(getFAForComponent(cta_is_following ? 'UNFOLLOW' : 'FOLLOW'));
    trackBP();

    props.toggleFollow(cta_follower_id!);
    props.markActivityAcknowledged(id);
  };

  const renderThumbnailCta = () => {
    const { cta_url, cta_thumbnail } = props.activity;
    return (
      <a
        href={cta_url ? cta_url : undefined}
        onClick={() => {
          trackFa(getFAForComponent('CTA_THUMBNAIL'));
          trackBP();
        }}
      >
        <img src={cta_thumbnail ? cta_thumbnail.src_4x : undefined} />
      </a>
    );
  };

  const renderLinkCta = (text: string, url: string) => {
    return (
      <LinkCTA
        href={url}
        text={text}
        onClick={() => {
          trackFa(getFAForComponent('CTA_VIEW'));
          trackBP();
        }}
      />
    );
  };

  const renderFollowCta = () => {
    const { cta_is_following, cta_follower_id } = props.activity;
    if (cta_is_following === null) {
      return null;
    }
    return (
      <FollowButton
        disabled={Boolean(props.pendingFollows[cta_follower_id!])}
        isFollowing={cta_is_following}
        onClick={handleFollowCtaClick}
      />
    );
  };

  const renderCta = () => {
    if (!props.showCta) {
      return null;
    }

    let cta, linkText, url;
    switch (props.activity.type) {
      case 'vodNewPurchase':
      case 'vodPreorderRelease':
      case 'vodRentalExpire':
      case 'clipTranscodeComplete':
      case 'clipLike':
      case 'clipComment':
      case 'commentMention':
      case 'commentReply':
      case 'clipCredit':
      case 'clipShare':
      case 'clipAddedToAlbum':
      case 'clipInvite':
      case 'clipMoved':
      case 'clipPublish':
      case 'clipPurged':
      case 'clipRenamed':
      case 'commentResolved':
      case 'svvComment':
      case 'reaction':
      case 'svvCommentReply':
      case 'clipCommentMention':
      case 'clipReviewStatusUpdated':
      case 'versionContentScan':
      case 'clipVersionDeleted':
      case 'clipVersionReplaced':
        cta = renderThumbnailCta();
        break;
      case 'channelFollow':
      case 'userFollow':
        cta = renderFollowCta();
        break;
      case 'userMessage':
        linkText = translate('View message');
        url = props.activity.url;
        cta = renderLinkCta(linkText, url);
        break;
    }

    if (!cta) {
      return null;
    }

    return (
      <Box
        maxWidth={rem(107)}
        position="absolute"
        top="50%"
        transform="translateY(-50%)"
        right={rem(16)}
        sx={{
          img: {
            verticalAlign: 'middle',
            borderRadius: rem(8),
          },
        }}
      >
        {cta}
      </Box>
    );
  };

  const activityTimestamp = new Date(Math.floor(timestamp * Math.pow(10, -6)));
  const activityReactionCode = props.activity?.metadata?.reaction_code;
  const activityWrapperHeight = wrapperRef.current?.getBoundingClientRect()?.height;
  const DEFAULT_AVATAR = 'https://i.vimeocdn.com/portrait/defaults-blue_64x64';

  return (
    <Box id="NotificationWrapper" ref={wrapperRef} position="relative">
      <Box
        margin="8px"
        borderRadius="md"
        background={displayed ? 'transparent' : 'fill-component'}
        _hover={{
          background: 'fill-component-hover',
          _dark: {
            background: 'fill-component-hover-dark',
          },
        }}
      >
        <Box
          as="a"
          href={subject_url || url}
          onClick={() => {
            trackFa(getFAForComponent('AVATAR'));
            trackBP();
          }}
          position="absolute"
          left={rem(16)}
          borderRadius="50%"
          top="50%"
          transform="translateY(-50%)"
          overflow="hidden"
          width={rem(40)}
          height={rem(40)}
          sx={{
            path: {
              fill: 'white',
            },
            img: {
              width: '100%',
            },
            svg: {
              width: '100%',
            },
          }}
        >
          <Avatar
            src={subject_thumbnail?.src_4x || DEFAULT_AVATAR}
            alt="user avatar image"
            size="md"
          ></Avatar>
        </Box>
        {activityReactionCode && (
          <Reaction containerHeight={activityWrapperHeight} reactionCode={activityReactionCode} />
        )}
        <Box
          as="a"
          href={url}
          onClick={handleActivityClick(url)}
          display="block"
          width="100%"
          padding={`${rem(16)} ${!props.showCta ? rem(24) : rem(145)}`}
          paddingLeft={rem(68)}
        >
          <Box
            id="NotificationMessage"
            color="text-primary"
            fontSize={rem(14)}
            sx={{
              wordWrap: 'break-word',
            }}
          >
            {text}
          </Box>
          <Box
            id="NotificationTime"
            as="time"
            color="text-secondary"
            cursor="pointer"
            fontSize={rem(12)}
            lineHeight={rem(18)}
          >
            {fromNow(activityTimestamp)}
          </Box>
        </Box>
        {renderCta()}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: ActivityState) => ({
  pendingFollows: state.pendingFollows,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  markActivityAcknowledged: (id: string) => dispatch((markActivityAcknowledged as any)(id)),
  toggleFollow: (userId: number) => dispatch((toggleFollow as any)(userId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActivityContainer);
